/**
 * RF-07 · Calificación de indicadores.
 *
 * Registra la nota de un evaluador sobre un indicador en una ejecución. Tres
 * campos NO se envían porque los calcula el backend y sobrescribe lo que llegue:
 * `calificacion_final`, `id_grado` y `nivel_asignado`.
 *
 * Una nota nula no cuenta como cero: la nota final se repondera sobre las
 * dimensiones realmente presentes.
 *
 * Store Pinia: estado compartido del módulo y orquestación de sus servicios.
 * Las vistas no llaman a `http` ni a los servicios directamente; pasan por aquí.
 * Los servicios no lanzan, así que las acciones interpretan valores neutros
 * (`[]`, `null`, `false`) como fallo.
 */

import { defineStore } from 'pinia'
import type EvaluacionIndicador from '@/interfaces/EvaluacionIndicador'
import type { EvaluacionIndicadorUpsertPayload } from '@/interfaces/EvaluacionIndicador'
import { getEvaluacionesIndicadorService, getEvaluacionIndicadorByIdService, crearEvaluacionIndicadorService, updateEvaluacionIndicadorService, guardarEvaluacionesBulkService} from '@/services/EvaluacionIndicadorService'
import type { EvaluacionBulkPayload, EvaluacionBulkResultado } from '@/services/EvaluacionIndicadorService'

/** Store `evaluacionesIndicadores`. Ver la cabecera del archivo para el contrato del módulo. */
export const useGestionEvaluacionesIndicadores = defineStore('evaluacionesIndicadores', {
  state: () => ({
    evaluaciones: [] as EvaluacionIndicador[],
    evaluacionActual: null as EvaluacionIndicador | null
  }),

  actions: {
    async cargar(params: { id_version?: number; id_indicador?: number; id_usuario?: number }): Promise<void> {
      this.evaluaciones = await getEvaluacionesIndicadorService(params)
    },

    async cargarPorId(id: number): Promise<EvaluacionIndicador | null> {
      const ev = await getEvaluacionIndicadorByIdService(id)
      this.evaluacionActual = ev
      return ev
    },

    async crearOActualizar(
      payload: EvaluacionIndicadorUpsertPayload
    ): Promise<{ ok: boolean; data?: EvaluacionIndicador; errors?: Record<string, string[]>; status?: number }> {
      const result = await crearEvaluacionIndicadorService(payload)
      if (result.ok && result.data) {
        const idUsuario = payload.id_usuario ?? result.data.id_usuario
        const idx = this.evaluaciones.findIndex(
          e => e.id_version === payload.id_version &&
               e.id_indicador === payload.id_indicador &&
               e.id_usuario === idUsuario
        )
        if (idx !== -1) {
          this.evaluaciones.splice(idx, 1, result.data)
        } else {
          this.evaluaciones.push(result.data)
        }
        this.evaluacionActual = result.data
      }
      return result
    },

    /**
     * Guardado masivo: todas las calificaciones en UNA petición (en vez de una
     * por indicador). Tras guardar conviene recargar con `cargar()`.
     */
    async guardarBulk(payload: EvaluacionBulkPayload): Promise<EvaluacionBulkResultado> {
      return await guardarEvaluacionesBulkService(payload)
    },

    async actualizar(
      id: number,
      payload: Record<string, unknown>
    ): Promise<{ ok: boolean; data?: EvaluacionIndicador; errors?: Record<string, string[]>; status?: number }> {
      const result = await updateEvaluacionIndicadorService(id, payload)
      if (result.ok && result.data) {
        const idx = this.evaluaciones.findIndex(e => e.id === id)
        if (idx !== -1) this.evaluaciones.splice(idx, 1, result.data)
        this.evaluacionActual = result.data
      }
      return result
    }
  }
})
